import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Wallet, Plus, Pencil, Trash2, Sparkles, ChevronLeft, ChevronRight,
  TrendingDown, AlertTriangle, CheckCircle2, Info, X, RefreshCw,
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useBudget, useBudgetComparison } from '@hooks/useBudget';
import PageHeader from '@components/layout/PageHeader';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import PageTutorial from '@components/common/PageTutorial';
import CurrencyInput from '@components/common/CurrencyInput';
import { formatCurrency } from '@utils/formatters';
import { clsx } from 'clsx';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const MONTHS_LABEL = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (monthStr) => {
  const [year, month] = monthStr.split('-');
  return `${MONTHS_LABEL[parseInt(month) - 1]} ${year}`;
};

const addMonth = (monthStr, delta) => {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const getComparisonStartMonth = (currentMonth) => addMonth(currentMonth, -5);

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  ok: {
    bar: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    icon: CheckCircle2,
    label: 'No prazo',
  },
  attention: {
    bar: 'bg-yellow-400',
    text: 'text-yellow-600 dark:text-yellow-400',
    badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    icon: Info,
    label: 'Atenção',
  },
  warning: {
    bar: 'bg-orange-500',
    text: 'text-orange-600 dark:text-orange-400',
    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    icon: AlertTriangle,
    label: 'Alerta',
  },
  exceeded: {
    bar: 'bg-red-500',
    text: 'text-red-600 dark:text-red-400',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: AlertTriangle,
    label: 'Estourado',
  },
};

// ─── Schema ──────────────────────────────────────────────────────────────────
const budgetSchema = z.object({
  plannedAmount: z.coerce.number().min(1, 'Informe um valor maior que zero'),
});

// ─── Category Row ─────────────────────────────────────────────────────────────
const CategoryRow = ({ item, onEdit, onDelete }) => {
  const pct = Math.min(100, item.percentage);
  const status = item.planned > 0 ? item.status : null;
  const cfg = status ? STATUS_CONFIG[status] : null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-3 mb-3">
        {/* Ícone */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
          style={{ backgroundColor: item.categoryColor ? `${item.categoryColor}20` : '#6366f120' }}
        >
          {item.categoryIcon || '📁'}
        </div>

        {/* Nome + status */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
              {item.categoryName}
            </span>
            {cfg && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.badge}`}>
                {item.percentage.toFixed(0)}% — {cfg.label}
              </span>
            )}
            {!status && item.realized > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                Sem orçamento
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            <span>{formatCurrency(item.realized)} realizado</span>
            {item.planned > 0 && (
              <>
                <span>·</span>
                <span>{formatCurrency(item.planned)} planejado</span>
              </>
            )}
          </div>
        </div>

        {/* Ações */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onEdit(item)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Editar orçamento"
          >
            <Pencil size={14} />
          </button>
          {item.planned > 0 && (
            <button
              onClick={() => onDelete(item)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Remover orçamento"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {item.planned > 0 && (
        <div className="space-y-1">
          <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${cfg?.bar || 'bg-primary-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500">
            <span>
              {item.remaining >= 0
                ? `Restam ${formatCurrency(item.remaining)}`
                : `Excedeu ${formatCurrency(Math.abs(item.remaining))}`}
            </span>
            <span>{pct.toFixed(0)}%</span>
          </div>
        </div>
      )}

      {/* Sem orçamento mas com gastos */}
      {!item.planned && item.realized > 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic mt-1">
          Defina um limite para controlar estes gastos
        </p>
      )}
    </div>
  );
};

// ─── Custom chart tooltip ─────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 shadow-lg text-sm">
      <p className="font-semibold text-gray-900 dark:text-white mb-2">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.fill }} />
          <span className="text-gray-500 dark:text-gray-400">{p.name}:</span>
          <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Budget = () => {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth);
  const [editingItem, setEditingItem] = useState(null); // {categoryId, categoryName, planned, month}
  const [deletingItem, setDeletingItem] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [aiComment, setAiComment] = useState(null);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [acceptedSuggestions, setAcceptedSuggestions] = useState({});

  const comparisonStart = useMemo(() => getComparisonStartMonth(selectedMonth), [selectedMonth]);

  const {
    categories,
    allCategories,
    totals,
    isLoading,
    setBudget,
    deleteBudget,
    suggestBudget,
    isSaving,
  } = useBudget(selectedMonth);

  const { data: comparisonData, isLoading: isLoadingComparison } = useBudgetComparison(
    comparisonStart,
    selectedMonth
  );

  // ── form
  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(budgetSchema),
    defaultValues: { plannedAmount: '' },
  });

  // ── navigation
  const goToPrevMonth = () => setSelectedMonth(m => addMonth(m, -1));
  const goToNextMonth = () => {
    const next = addMonth(selectedMonth, 1);
    if (next <= getCurrentMonth()) setSelectedMonth(next);
  };
  const isCurrentMonth = selectedMonth === getCurrentMonth();

  // ── edit modal
  const openEdit = (item) => {
    setEditingItem(item);
    reset({ plannedAmount: item.planned || '' });
  };
  const closeEdit = () => { setEditingItem(null); reset(); };

  const onSaveBudget = async (data) => {
    await setBudget({
      categoryId: editingItem.categoryId,
      month: selectedMonth,
      plannedAmount: data.plannedAmount,
    });
    closeEdit();
  };

  // ── delete
  const onConfirmDelete = async () => {
    await deleteBudget(deletingItem.categoryId, selectedMonth);
    setDeletingItem(null);
  };

  // ── AI suggestions
  const handleSuggest = async () => {
    setLoadingSuggest(true);
    try {
      const result = await suggestBudget();
      setSuggestions(result.suggestions || []);
      setAiComment(result.aiComment || null);
      const initial = {};
      (result.suggestions || []).forEach(s => { initial[s.categoryId] = true; });
      setAcceptedSuggestions(initial);
      setShowSuggestions(true);
    } finally {
      setLoadingSuggest(false);
    }
  };

  const onApplySuggestions = async () => {
    const toApply = suggestions.filter(s => acceptedSuggestions[s.categoryId]);
    for (const s of toApply) {
      await setBudget({
        categoryId: s.categoryId,
        month: selectedMonth,
        plannedAmount: s.suggestedAmount,
      });
    }
    setShowSuggestions(false);
  };

  // ── chart data
  const chartData = useMemo(() => {
    if (!comparisonData) return [];
    return comparisonData.map(item => ({
      name: item.monthLabel,
      Planejado: item.planned,
      Realizado: item.realized,
    }));
  }, [comparisonData]);

  // ── separar categorias com e sem orçamento
  const withBudget = categories.filter(c => c.planned > 0);
  const withoutBudget = allCategories.filter(c => c.planned === 0 && c.realized > 0);
  const untracked = allCategories.filter(c => c.planned === 0 && c.realized === 0);

  const totalPct = totals.planned > 0 ? Math.min(100, (totals.realized / totals.planned) * 100) : 0;

  return (
    <div className="space-y-6">
      <PageTutorial
        pageId="budget"
        title="Orcamento"
        steps={[
          { text: 'Clique no lapis de qualquer categoria para definir quanto planeja gastar naquele mes.' },
          { text: 'As barras de progresso mostram em tempo real quanto voce ja comprometeu de cada limite.' },
          { text: 'Use "Sugerir com IA" para gerar limites automaticos baseados na sua media dos ultimos 3 meses.' },
        ]}
      />

      {/* Header */}
      <PageHeader
        title="Orcamento"
        subtitle="Planeje e acompanhe seus gastos por categoria"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={handleSuggest}
              isLoading={loadingSuggest}
              leftIcon={<Sparkles size={16} />}
            >
              Sugerir com IA
            </Button>
            <Button
              leftIcon={<Plus size={16} />}
              onClick={() => openEdit({ categoryId: '', categoryName: 'Categoria', planned: 0 })}
            >
              Definir limite
            </Button>
          </div>
        }
      />

      {/* Month Navigator */}
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={goToPrevMonth}
          className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="text-base font-semibold text-gray-900 dark:text-white w-52 text-center">
          {formatMonthLabel(selectedMonth)}
        </span>
        <button
          onClick={goToNextMonth}
          disabled={isCurrentMonth}
          className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total planejado</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(totals.planned || 0)}
              </p>
              <p className="text-xs text-gray-400 mt-1">{withBudget.length} categorias com limite</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total realizado</p>
              <p className={clsx(
                'text-2xl font-bold',
                totalPct >= 100
                  ? 'text-red-600 dark:text-red-400'
                  : totalPct >= 80
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-gray-900 dark:text-white'
              )}>
                {formatCurrency(totals.realized || 0)}
              </p>
              {totals.planned > 0 && (
                <div className="mt-2">
                  <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-full transition-all',
                        totalPct >= 100 ? 'bg-red-500' : totalPct >= 80 ? 'bg-orange-500' : 'bg-primary-500'
                      )}
                      style={{ width: `${totalPct}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{totalPct.toFixed(0)}% do total</p>
                </div>
              )}
            </div>
            <div className={clsx(
              'rounded-2xl border p-4',
              (totals.remaining ?? 0) < 0
                ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                : 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800'
            )}>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Saldo restante</p>
              <p className={clsx(
                'text-2xl font-bold',
                (totals.remaining ?? 0) < 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-emerald-700 dark:text-emerald-400'
              )}>
                {formatCurrency(totals.remaining ?? 0)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {(totals.remaining ?? 0) < 0 ? 'Acima do planejado' : 'Dentro do planejado'}
              </p>
            </div>
          </div>

          {/* Categories com orçamento */}
          {withBudget.length > 0 ? (
            <div>
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Categorias com limite definido
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {withBudget.map(item => (
                  <CategoryRow
                    key={item.categoryId}
                    item={item}
                    onEdit={openEdit}
                    onDelete={setDeletingItem}
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyState
              icon={<Wallet size={32} className="text-gray-400" />}
              title="Nenhum limite definido ainda"
              description="Defina limites por categoria para acompanhar seus gastos e receber alertas automaticos."
              action={
                <div className="flex gap-2">
                  <Button variant="secondary" leftIcon={<Sparkles size={16} />} onClick={handleSuggest} isLoading={loadingSuggest}>
                    Sugerir com IA
                  </Button>
                  <Button leftIcon={<Plus size={16} />} onClick={() => openEdit({ categoryId: '', categoryName: '', planned: 0 })}>
                    Definir manualmente
                  </Button>
                </div>
              }
            />
          )}

          {/* Categorias sem orçamento mas com gastos */}
          {withoutBudget.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-500" />
                Categorias sem limite (com gastos)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {withoutBudget.map(item => (
                  <CategoryRow
                    key={item.categoryId}
                    item={item}
                    onEdit={openEdit}
                    onDelete={setDeletingItem}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Historical comparison chart */}
          {(chartData.length > 0 || isLoadingComparison) && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Historico — Planejado vs Realizado
                </h2>
                <span className="text-xs text-gray-400">Ultimos 6 meses</span>
              </div>
              {isLoadingComparison ? (
                <div className="h-48 flex items-center justify-center">
                  <RefreshCw size={20} className="animate-spin text-gray-400" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} barSize={18} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-gray-100 dark:stroke-gray-700" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: 'currentColor' }}
                      className="text-gray-400"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'currentColor' }}
                      className="text-gray-400"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend
                      wrapperStyle={{ fontSize: '12px' }}
                      formatter={(value) => <span className="text-gray-600 dark:text-gray-400">{value}</span>}
                    />
                    <Bar dataKey="Planejado" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Realizado" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal: editar/criar orçamento por categoria */}
      <Modal
        isOpen={!!editingItem}
        onClose={closeEdit}
        title={editingItem?.categoryId ? `Limite: ${editingItem.categoryName}` : 'Definir limite de categoria'}
        size="sm"
      >
        <form onSubmit={handleSubmit(onSaveBudget)} className="space-y-5">
          {/* Se não foi passado categoryId específico, mostrar seletor de categoria */}
          {!editingItem?.categoryId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Categoria
              </label>
              <select
                className="block w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                onChange={(e) => {
                  const cat = allCategories.find(c => c.categoryId === e.target.value);
                  if (cat) {
                    setEditingItem(prev => ({ ...prev, categoryId: cat.categoryId, categoryName: cat.categoryName }));
                    reset({ plannedAmount: cat.planned || '' });
                  }
                }}
                value={editingItem?.categoryId || ''}
              >
                <option value="">Selecione uma categoria</option>
                {allCategories.map(c => (
                  <option key={c.categoryId} value={c.categoryId}>
                    {c.categoryIcon} {c.categoryName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Limite mensal
            </label>
            <Controller
              name="plannedAmount"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label=""
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                  onBlur={field.onBlur}
                  placeholder="R$ 0,00"
                  error={errors.plannedAmount?.message}
                />
              )}
            />
            {editingItem?.planned > 0 && (
              <p className="text-xs text-gray-400 mt-1.5">
                Limite atual: {formatCurrency(editingItem.planned)}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={closeEdit}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSaving} disabled={!editingItem?.categoryId}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal: confirmar exclusão */}
      <Modal
        isOpen={!!deletingItem}
        onClose={() => setDeletingItem(null)}
        title="Remover limite"
        size="sm"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          Deseja remover o limite de{' '}
          <span className="font-semibold text-gray-900 dark:text-white">
            {deletingItem?.categoryName}
          </span>
          {' '}para {formatMonthLabel(selectedMonth)}? Os gastos registrados nao serao afetados.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeletingItem(null)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={onConfirmDelete} isLoading={isSaving}>
            Remover
          </Button>
        </div>
      </Modal>

      {/* Modal: sugestoes de IA */}
      <Modal
        isOpen={showSuggestions}
        onClose={() => setShowSuggestions(false)}
        title="Sugestoes de Orcamento — IA"
        size="lg"
      >
        <div className="space-y-4">
          {aiComment ? (
            <div className="flex items-start gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl">
              <Sparkles size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-purple-700 dark:text-purple-400 mb-1">
                  Analise do Consultor IA
                </p>
                <p className="text-sm text-purple-800 dark:text-purple-300 leading-relaxed">
                  {aiComment}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl">
              <Sparkles size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-purple-800 dark:text-purple-300">
                Valores sugeridos com base na sua media de gastos dos ultimos 3 meses, com uma margem de 10% para imprevistos.
                Desmarque as categorias que nao deseja aplicar.
              </p>
            </div>
          )}

          {suggestions.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              Historico insuficiente. Registre transacoes por pelo menos 1 mes para obter sugestoes.
            </p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {suggestions.map(s => (
                <label
                  key={s.categoryId}
                  className={clsx(
                    'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors',
                    acceptedSuggestions[s.categoryId]
                      ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/10'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 opacity-60'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={!!acceptedSuggestions[s.categoryId]}
                    onChange={(e) => setAcceptedSuggestions(prev => ({
                      ...prev,
                      [s.categoryId]: e.target.checked,
                    }))}
                    className="rounded text-primary-600"
                  />
                  <span className="text-lg">{s.categoryIcon || '📁'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{s.categoryName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Media: {formatCurrency(s.averageSpent)} · Base: {s.basedOnMonths} {s.basedOnMonths === 1 ? 'mes' : 'meses'}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-primary-700 dark:text-primary-400 whitespace-nowrap">
                    {formatCurrency(s.suggestedAmount)}
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setShowSuggestions(false)}>
              Cancelar
            </Button>
            {suggestions.length > 0 && (
              <Button
                onClick={onApplySuggestions}
                isLoading={isSaving}
                disabled={!Object.values(acceptedSuggestions).some(Boolean)}
                leftIcon={<CheckCircle2 size={15} />}
              >
                Aplicar selecionados ({Object.values(acceptedSuggestions).filter(Boolean).length})
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Budget;
